import React from 'react';
import { Box, Button, Chip, Menu, MenuItem } from '@mui/material';
import i18n from '../../i18n';
import { FirstMenu } from '../../models/first-menu.entity';
import { ReactComponent as CloseFillIcon } from '../../static/images/svg/mingcute_close-fill.svg';
import { ReactComponent as PlusRoundIcon } from '../../static/images/svg/ic_round-plus.svg';
import './scss/first-level-menu-tag-picker.scss';

export type FirstLevelMenuTagPickerProps = {
    /** 已选一级菜单 id（顺序与保存一致） */
    selectedIds: number[];
    /** 全量一级菜单，用于展示名称与下拉可选项 */
    allMenus: FirstMenu[];
    /** 选择下拉中的一项后回调 */
    onAdd: (mainGroupId: number) => void;
    /** 点击标签关闭时回调 */
    onRemove: (mainGroupId: number) => void;
    /** 新增按钮文案的 i18n key */
    addButtonI18nKey: string;
    /** 标签删除钮无障碍文案的 i18n key，支持 `{{name}}` 插值 */
    removeChipAriaI18nKey: string;
    /** 标签列表 `aria-label` 的 i18n key */
    listAriaLabelI18nKey: string;
    /**
     * 为 true 时外层为描边圆角容器（RWA 外卖区 Figma 1893:21764）；
     * false 时仅 flex 换行（KWA「不参与轮次推进」区）。
     */
    borderedContainer?: boolean;
    /** 根节点额外 className */
    className?: string;
};

type State = {
    /** 新增下拉的锚点 */
    addMenuAnchorEl: HTMLElement | null;
};

/**
 * 云 RBP 子功能内「一级菜单多选」：已选 Tag + 虚线「新增」+ 过滤后的 MUI Menu。
 * 与 KWA 设置、RWA 外卖菜单共用，样式见 `first-level-menu-tag-picker.scss`（对齐 Figma）。
 */
export default class FirstLevelMenuTagPicker extends React.Component<FirstLevelMenuTagPickerProps, State> {
    public constructor(props: FirstLevelMenuTagPickerProps) {
        super(props);
        this.state = { addMenuAnchorEl: null };
    }

    public render(): React.ReactNode {
        const { selectedIds, allMenus, onAdd, onRemove, addButtonI18nKey, removeChipAriaI18nKey, listAriaLabelI18nKey, borderedContainer, className } =
            this.props;
        const { addMenuAnchorEl } = this.state;
        const availableToAdd = allMenus.filter(
            (m) => m.main_group_id != null && !selectedIds.includes(m.main_group_id as number),
        );
        return (
            <Box
                className={[
                    'first-level-menu-tag-picker',
                    borderedContainer ? 'first-level-menu-tag-picker--bordered' : '',
                    className || '',
                ]
                    .filter(Boolean)
                    .join(' ')}
            >
                <Box
                    className="first-level-menu-tag-picker__tags"
                    role="list"
                    aria-label={i18n.t(listAriaLabelI18nKey)}
                >
                    {selectedIds.map((id) => {
                        const item = allMenus.find((m) => m.main_group_id === id);
                        const name = String(item?.main_group_name ?? id);
                        return (
                            <Chip
                                key={id}
                                className="first-level-menu-tag-picker__menu-tag"
                                role="listitem"
                                label={name}
                                onDelete={() => onRemove(id)}
                                deleteIcon={
                                    <Box
                                        component="span"
                                        onMouseDown={(e) => e.stopPropagation()}
                                        aria-label={i18n.t(removeChipAriaI18nKey, { name })}
                                        sx={{ display: 'inline-flex', alignItems: 'center' }}
                                    >
                                        <CloseFillIcon width={24} height={24} aria-hidden />
                                    </Box>
                                }
                            />
                        );
                    })}
                    <Button
                        className="first-level-menu-tag-picker__add-menu-btn"
                        onClick={(e) => {
                            this.setState({ addMenuAnchorEl: e.currentTarget });
                        }}
                        disabled={availableToAdd.length === 0}
                    >
                        {i18n.t(addButtonI18nKey)}
                        <PlusRoundIcon width={32} height={32} className="first-level-menu-tag-picker__add-menu-icon" aria-hidden />
                    </Button>
                </Box>
                <Menu
                    anchorEl={addMenuAnchorEl}
                    open={Boolean(addMenuAnchorEl)}
                    onClose={() => {
                        this.setState({ addMenuAnchorEl: null });
                    }}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                >
                    {availableToAdd.map((item) => {
                        const mid = item.main_group_id as number;
                        return (
                            <MenuItem
                                key={mid}
                                onClick={() => {
                                    onAdd(mid);
                                    this.setState({ addMenuAnchorEl: null });
                                }}
                            >
                                {String(item.main_group_name ?? mid)}
                            </MenuItem>
                        );
                    })}
                </Menu>
            </Box>
        );
    }
}
